// noinspection JSUnusedGlobalSymbols

import {
    GET_PAST_VISITS_FROM_FB,
    migrateFutureToPastVisits,
    updateAllVisits
} from '../screens/App/snippets/Tests&Visits/utils';
import api from '../services/api';
import { eventBus } from '../services/eventBus';
import firebase from '../services/firebase';
import { rescheduleVisitNotifications } from '../services/notifications';
import userService from '../services/user';
import {
    putIntoStore,
    SCHEME
} from '../store';
import { debouncer } from '../utils';
import { decryptCached } from '../utils/crypto';
import { createReducerFor } from './utils';

const type = 'VISITS';
export const CREATED_MANUALLY = 'created_manually';

const updateOldVisits = (visits, userInfo) => {
    // EPMAIMD-5300. this code section needs while users have old personIds value in local storage
    if (visits && visits.length) {
        const people = [ userInfo.me, ...(userInfo.relatives || []) ];
        if (people.length) {
            people.forEach(({ profileId }) => {
                visits.forEach((visit) =>
                    visit.personId === profileId && visit.profileId !== profileId
                        ? Object.assign(visit, { profileId })
                        : null,
                );
            });
        }
    }
    return visits;
};

const debounce = debouncer(10000);
/**
 * Forces visits data re-query on backend.
 * Called at user sync.
 */
export const pingVisits = () => async (dispatch, getState) => {
    const {
        visits: { data: prevVisits },
    } = getState();

    setTimeout(async () => {
        const resp = await api.pingVisits();

        if (!GET_PAST_VISITS_FROM_FB && resp?.data) {
            const parsedData = JSON.parse(resp?.data || '[]');
            updateAllVisits(parsedData);
        }
    }, 100);

    dispatch({
        type,
        payload: {
            error: null,
            data:
                prevVisits &&
                prevVisits.map((e) => ({
                    ...e,
                    updated: false,
                })),
            lastPingStarted: Date.now(),
        },
    });
};

export const resetVisits = () => async (dispatch) => {
    dispatch({
        type,
        payload: {
            error: null,
            data: null,
            isLoading: false,
        },
    });
};

export const addImportedVisit = (visit) => (dispatch, getState) => {
    const { importedVisits = [] } = getState()?.visits ?? {};
    const newState = [ ...importedVisits, visit ];
    dispatch({
        type,
        payload: {
            importedVisits: newState,
            clearImported: false,
        },
    });
};

export const clearImportedVisits = () => (dispatch) => {
    dispatch({
        type,
        payload: { importedVisits: [] },
    });
};

/**
 * Synchronizes visits data from firebase profile document.
 * Called every time profile document is changed.
 */
export const syncVisits = (visitList) => async (dispatch, getState) => {
    try {
        const userInfo = await userService.getUserInfo();
        const {
            hashKey,
            storeKey,
        } = userInfo;
        const {
            visits: {
                lastPingStarted,
                clearImported = false,
            },
        } = getState();

        if (!hashKey || !storeKey) {
            dispatch({
                type,
                payload: {
                    error: null,
                    data: null,
                    isLoading: false,
                },
            });
            return;
        }
        // console.warn(`syncVisits : time=${Date.now()} `);

        dispatch({
            type,
            payload: {
                error: null,
                isLoading: true,
            },
        });

        const encrypted = visitList || (await firebase.getUserProfile(storeKey)).visitList || [];

        const decrypted = encrypted.map((e) => decryptCached(e, hashKey));

        if (!GET_PAST_VISITS_FROM_FB) {
            migrateFutureToPastVisits(decrypted);
        }

        const data = updateOldVisits(decrypted, userInfo)
            .map((e) => ({
                ...e,
                updated: e.lastUpdated >= lastPingStarted,
                isManageable:
                    userInfo?.relatives?.find((r) => r.profileId === e.profileId)?.isVisitsManageable ||
                    e.profileId === userInfo?.userId,
            }));

        // debounce to avoid massive serie of re-scheduling in case of many providers.
        debounce(() => rescheduleVisitNotifications(data));

        // const actualData = data.filter(e => (isFutureVisit(e.endDate) && e.status === 'reserved'));
        const someUpdated = data.some((e) => e.updated || e.status === CREATED_MANUALLY);

        const allVisitsAreUnmanageable = !data.some((e) => e.isManageable);
        const clear = clearImported ? { importedVisit: [] } : { clearImported: true };
        dispatch({
            type,
            payload: {
                data,
                isLoading: !!(data.length && !someUpdated && !allVisitsAreUnmanageable),
                ...clear,
            },
        });
        // It is necessary to ensure that in case of a visit change (rescheduling), the data returned from loadProviderCodes is available.
        eventBus.emit('visitsReady', {
            data,
            profileId: userInfo?.userId,
            hashKey,
        });
    } catch (ex) {
        dispatch({
            type,
            payload: {
                error: ex,
                isLoading: false,
            },
        });
    }
};

const getError = (result) => {
    try {
        return JSON.parse(result?.data)?.title ?? 'Error';
    } catch {
        return 'Error';
    }
};

const getFullError = (result) => {
    try {
        return JSON.parse(result?.data);
    } catch {
        return result?.data;
    }
};

export const loadVisits =
    ({
        profileId,
        clinicId,
        onError,
        onSuccess,
        profiles = [],
        cb,
    }) =>
        async (dispatch) => {
            try {
                dispatch({
                    type,
                    payload: { isLoadingOrders: true },
                });
                const res = await api.loadVisits(profileId, clinicId, profiles);
                const data = await res.info();
                if (data.status === 200) {
                    onSuccess && onSuccess();
                }
                if (data?.status > 399) {
                    onError && onError(data?.message || getError(res), getFullError(res));
                }
                if (cb) {
                    const parsedData = JSON.parse(data?.body ?? res?.data ?? '[]');
                    if (Array.isArray(parsedData)) {
                        cb(parsedData);
                    }
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.log('Error load visits', e);

                onError && onError(e);
            }
            dispatch({
                type,
                payload: { isLoadingOrders: false },
            });
        };

export const startResultOrReportLoading = () => (dispatch) =>
    dispatch({
        type,
        payload: { isResultOrReportLoading: true },
    });

export const stopResultOrReportLoading = () => (dispatch) =>
    dispatch({
        type,
        payload: { isResultOrReportLoading: false },
    });

export const setResultOrReportError = (error) => (dispatch) =>
    dispatch({
        type,
        payload: { error },
    });

export const resetVisitsFilter = () => {
    try {
        putIntoStore(SCHEME.VISITS_FILTER, {
            visitors: [],
            byAttachments: false,
            byReports: false,
            clinics: [],
            doctors: [],
        });
        putIntoStore(SCHEME.VISITS_PREFILTER_VISITORS, []);
        putIntoStore(SCHEME.VISITS_PREFILTER_CLINICS, []);
        putIntoStore(SCHEME.VISITS_PREFILTER_DOCTORS, []);
        putIntoStore(SCHEME.VISITS_PREFILTER_ATTACHMENTS, false);
        putIntoStore(SCHEME.VISITS_PREFILTER_REPORTS, false);
    } catch {
        //
    }
};
export default createReducerFor(type);
